import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Sun, Moon, MessageCircle, Heart } from 'lucide-react';
import { DISCORD_INVITE_URL } from '../config/discord';

/* ────────────────────────────────────────────────────────── */
/*  Feature‑section visual mockups (animated on scroll)       */
/* ────────────────────────────────────────────────────────── */
const PracticeMock = ({ animate }) => {
  const [answered, setAnswered] = useState(0);
  const [timer, setTimer] = useState(9932);

  useEffect(() => {
    if (!animate) return;
    const id = setInterval(() => {
      setTimer(t => t > 0 ? t - 1 : t);
      setAnswered(a => a < 12 ? a + 1 : a);
    }, 400);
    return () => clearInterval(id);
  }, [animate]);

  const hh = String(Math.floor(timer / 3600)).padStart(2, '0');
  const mm = String(Math.floor((timer % 3600) / 60)).padStart(2, '0');
  const ss = String(timer % 60).padStart(2, '0');

  return (
    <div className="w-full max-w-md rounded-2xl bg-background border border-border/60 shadow-lg overflow-hidden">
      <div className="px-5 py-3 border-b border-border/40 flex justify-between items-center">
        <span className="font-mono text-sm font-bold text-foreground tabular-nums">{`${hh}:${mm}:${ss}`}</span>
        <span className="text-xs text-muted-foreground">Q {Math.min(answered + 1, 20)} / 90</span>
      </div>
      <div className="p-5 space-y-4">
        <div className="flex flex-wrap gap-1.5">
          {[...Array(20)].map((_, i) => {
            const isGreen = i < Math.min(answered, 6);
            const isRed = i >= 6 && i < Math.min(answered, 10);
            const isCurrent = i === Math.min(answered, 19);
            return (
              <div key={i} className={`w-7 h-7 rounded-lg text-[11px] flex items-center justify-center font-medium transition-all duration-300
                ${isGreen ? 'bg-green-500/20 text-green-600 scale-100' : isRed ? 'bg-red-500/20 text-red-500 scale-100' : isCurrent ? 'bg-primary text-white ring-2 ring-primary/30 scale-110' : 'bg-muted text-muted-foreground'}`}>{i + 1}</div>
            );
          })}
        </div>
        <div className="rounded-xl bg-muted/40 p-4 space-y-2">
          <div className={`h-3 rounded bg-muted transition-all duration-700 ${animate ? 'w-full' : 'w-0'}`} />
          <div className={`h-3 rounded bg-muted transition-all duration-700 delay-100 ${animate ? 'w-4/5' : 'w-0'}`} />
          <div className={`h-3 rounded bg-muted transition-all duration-700 delay-200 ${animate ? 'w-3/5' : 'w-0'}`} />
        </div>
        <div className="flex gap-2">
          <div className="h-10 flex-1 rounded-lg bg-primary text-white flex items-center justify-center text-sm font-medium">Save & Next</div>
          <div className="h-10 px-4 rounded-lg border border-border flex items-center justify-center text-sm text-muted-foreground">Mark for Review</div>
        </div>
      </div>
    </div>
  );
};

const AnalyticsMock = ({ animate }) => {
  const [barWidths, setBarWidths] = useState([0, 0, 0, 0]);

  useEffect(() => {
    if (!animate) return;
    const timer = setTimeout(() => setBarWidths([92, 74, 88, 81]), 300);
    return () => clearTimeout(timer);
  }, [animate]);

  return (
    <div className="w-full max-w-md rounded-2xl bg-background border border-border/60 shadow-lg overflow-hidden">
      <div className="px-5 py-3 border-b border-border/40">
        <span className="text-sm font-medium text-foreground">Performance</span>
      </div>
      <div className="p-5 space-y-4">
        <div className="grid grid-cols-3 gap-3 text-center">
          {[{ v: '85%', l: 'Accuracy', c: 'text-emerald-500 bg-emerald-500/10' }, { v: '1.2m', l: 'Avg Time', c: 'text-blue-500 bg-blue-500/10' }, { v: '12', l: 'Streak', c: 'text-amber-500 bg-amber-500/10' }].map((s, i) => (
            <div key={s.l} className={`p-3 rounded-xl ${s.c.split(' ').slice(1).join(' ')} transition-all duration-500 ${animate ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`} style={{ transitionDelay: `${i * 150}ms` }}>
              <div className={`text-xl font-bold ${s.c.split(' ')[0]}`}>{s.v}</div>
              <div className="text-[10px] text-muted-foreground mt-0.5">{s.l}</div>
            </div>
          ))}
        </div>
        <div className="space-y-2">
          {[{ l: 'Physics', c: 'bg-emerald-500' }, { l: 'Chemistry', c: 'bg-blue-500' }, { l: 'Maths', c: 'bg-purple-500' }, { l: 'Biology', c: 'bg-amber-500' }].map((s, i) => (
            <div key={s.l} className="flex items-center gap-3 text-xs">
              <span className="w-20 text-muted-foreground">{s.l}</span>
              <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                <div className={`h-full ${s.c} rounded-full transition-all duration-1000 ease-out`} style={{ width: `${barWidths[i]}%`, transitionDelay: `${400 + i * 200}ms` }} />
              </div>
              <span className="w-8 text-right text-muted-foreground font-medium tabular-nums">{barWidths[i]}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const MistakesMock = ({ animate }) => {
  const [showItems, setShowItems] = useState(0);

  useEffect(() => {
    if (!animate) return;
    let i = 0;
    const id = setInterval(() => {
      i++;
      if (i <= 4) setShowItems(i);
      else clearInterval(id);
    }, 350);
    return () => clearInterval(id);
  }, [animate]);

  return (
    <div className="w-full max-w-md rounded-2xl bg-background border border-border/60 shadow-lg overflow-hidden">
      <div className="px-5 py-3 border-b border-border/40 flex items-center gap-2">
        <span className="text-sm font-medium text-foreground">Mistake Tracker</span>
        <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-500 font-medium">3 due for review</span>
      </div>
      <div className="p-5 space-y-3">
        <div className="grid grid-cols-3 gap-2 text-center">
          {[{ v: '23', l: 'Total', c: 'text-rose-500 bg-rose-500/10' }, { v: '8', l: 'Reviewed', c: 'text-emerald-500 bg-emerald-500/10' }, { v: '71%', l: 'Fixed', c: 'text-blue-500 bg-blue-500/10' }].map((s, i) => (
            <div key={s.l} className={`p-2 rounded-xl ${s.c.split(' ').slice(1).join(' ')} transition-all duration-500 ${i < showItems ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
              <div className={`text-lg font-bold ${s.c.split(' ')[0]}`}>{s.v}</div>
              <div className="text-[10px] text-muted-foreground">{s.l}</div>
            </div>
          ))}
        </div>
        <div className="space-y-2">
          {[
            { q: 'Organic: Nucleophilic substitution...', t: 'Conceptual', d: '2 days ago' },
            { q: 'Mechanics: Rotational inertia of...', t: 'Calculation', d: '3 days ago' },
            { q: 'Calculus: Integration by parts...', t: 'Silly mistake', d: '5 days ago' },
          ].map((m, i) => (
            <div key={i} className={`p-3 rounded-xl bg-muted/30 border border-border/30 transition-all duration-500 ${i + 1 <= showItems - 0 ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}`} style={{ transitionDelay: `${600 + i * 200}ms` }}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs text-foreground truncate">{m.q}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 font-medium">{m.t}</span>
                    <span className="text-[10px] text-muted-foreground">{m.d}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ChallengeMock = ({ animate }) => {
  const targets = [47, 43, 38, 35];
  const [scores, setScores] = useState([0, 0, 0, 0]);

  useEffect(() => {
    if (!animate) return;
    const duration = 1500;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setScores(targets.map(t => Math.floor(ease * t)));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [animate]);

  const players = [
    { n: 'Aarav', c: 'bg-blue-500' },
    { n: 'Priya', c: 'bg-green-500' },
    { n: 'Rahul', c: 'bg-purple-500' },
    { n: 'Sneha', c: 'bg-amber-500' },
  ];

  return (
    <div className="w-full max-w-md rounded-2xl bg-background border border-border/60 shadow-lg overflow-hidden">
      <div className="px-5 py-3 border-b border-border/40">
        <span className="text-sm font-medium text-foreground">Live Challenge</span>
      </div>
      <div className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex -space-x-2">
            {players.map((p, i) => (
              <div key={i} className={`w-9 h-9 rounded-full ${p.c} border-2 border-background flex items-center justify-center text-white text-xs font-bold transition-all duration-500 ${animate ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`} style={{ transitionDelay: `${i * 100}ms` }}>
                {p.n[0]}
              </div>
            ))}
          </div>
          <span className="text-xs font-medium text-muted-foreground">4 players</span>
        </div>
        <div className="space-y-2">
          {players.map((p, i) => (
            <div key={i} className={`flex items-center gap-3 transition-all duration-500 ${animate ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}`} style={{ transitionDelay: `${400 + i * 120}ms` }}>
              <span className="text-xs text-muted-foreground w-4">{i + 1}</span>
              <div className={`w-6 h-6 rounded-full ${p.c} flex items-center justify-center text-white text-[10px] font-bold`}>{p.n[0]}</div>
              <span className="text-sm text-foreground flex-1">{p.n}</span>
              <span className="text-sm font-bold text-foreground tabular-nums">{scores[i]}</span>
            </div>
          ))}
        </div>
        <div className="text-center pt-1">
          <span className="text-xs text-muted-foreground">Q 8/15 · 02:12 remaining</span>
        </div>
      </div>
    </div>
  );
};

/* ────────────────────────────────────────────────────────── */
/*  Main component                                            */
/* ────────────────────────────────────────────────────────── */
const Landing = () => {
  const navigate = useNavigate();
  const [isDark, setIsDark] = useState(false);
  const [examCount, setExamCount] = useState(0);
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [scrollProgress, setScrollProgress] = useState(0);
  const [heroReady, setHeroReady] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0.5, y: 0.5 });
  const [animatedStats, setAnimatedStats] = useState({ questions: 0, exams: 0, chapters: 0 });
  const sectionRefs = useRef({});
  const heroRef = useRef(null);

  useEffect(() => {
    const darkMode = localStorage.getItem('darkMode') === 'true';
    setIsDark(darkMode);
    if (darkMode) document.documentElement.classList.add('dark');
    loadExamCount();
    const heroTimer = setTimeout(() => setHeroReady(true), 100);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => new Set([...prev, entry.target.dataset.section]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -60px 0px' }
    );
    Object.values(sectionRefs.current).forEach(ref => { if (ref) observer.observe(ref); });

    const onScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(total > 0 ? window.scrollY / total : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => { clearTimeout(heroTimer); observer.disconnect(); window.removeEventListener('scroll', onScroll); };
  }, []);

  // Animate stat counters
  useEffect(() => {
    if (!visibleSections.has('stats')) return;
    const targets = { questions: 10000, exams: examCount || 17, chapters: 45 };
    const duration = 2000;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setAnimatedStats({
        questions: Math.floor(ease * targets.questions),
        exams: Math.floor(ease * targets.exams),
        chapters: Math.floor(ease * targets.chapters),
      });
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [visibleSections, examCount]);

  const loadExamCount = async () => {
    try {
      const backendUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
      const response = await fetch(`${backendUrl}/api/exams`);
      const data = await response.json();
      setExamCount(data?.length || 17);
    } catch { setExamCount(17); }
  };

  const toggleDarkMode = () => {
    const newDarkMode = !isDark;
    setIsDark(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
    newDarkMode ? document.documentElement.classList.add('dark') : document.documentElement.classList.remove('dark');
  };

  const handleHeroMouse = useCallback((e) => {
    if (!heroRef.current) return;
    const r = heroRef.current.getBoundingClientRect();
    setMousePos({ x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height });
  }, []);

  const setSectionRef = (key) => (el) => { sectionRefs.current[key] = el; };
  const isVisible = (key) => visibleSections.has(key);

  // Feature sections data
  const features = [
    {
      key: 'feat-practice',
      label: 'Practice Tests',
      color: 'text-primary',
      title: 'Structured practice that builds consistency',
      desc: 'Chapter-wise or full-length, timed or untimed. Pick a subject, pick a chapter, and start solving — your progress is tracked automatically.',
      cta: 'Try a practice test',
      href: '/test',
      Mock: PracticeMock,
    },
    {
      key: 'feat-analytics',
      label: 'Analytics',
      color: 'text-emerald-500',
      title: 'Know exactly where you stand',
      desc: 'Topic-wise accuracy, speed trends, mistake patterns, exam readiness scores — analytics that actually help you improve.',
      cta: 'View analytics',
      href: '/analytics',
      Mock: AnalyticsMock,
    },
    {
      key: 'feat-mistakes',
      label: 'Mistake Tracker',
      color: 'text-amber-500',
      title: 'Learn from every wrong answer',
      desc: 'Every mistake is logged, categorised, and queued for review. Spaced repetition helps you fix weak spots before exam day.',
      cta: 'See mistake tracker',
      href: '/mistakes',
      Mock: MistakesMock,
    },
    {
      key: 'feat-challenge',
      label: 'Challenges',
      color: 'text-rose-500',
      title: 'Turn studying into competition',
      desc: 'Create timed quiz battles, compete on leaderboards, and push each other to do better.',
      cta: 'Challenge a friend',
      href: '/challenges',
      Mock: ChallengeMock,
    },
  ];

  return (
    <div className="min-h-screen bg-background overflow-x-hidden relative">
      {/* Scroll progress */}
      <div className="fixed top-0 left-0 right-0 z-[60] h-[2px] bg-transparent pointer-events-none">
        <div className="h-full bg-primary/80 will-change-transform" style={{ width: `${scrollProgress * 100}%` }} />
      </div>

      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <img src="/logo192.png" alt="ExamDex" className="w-8 h-8 object-contain" />
            <span className="text-lg font-bold text-foreground tracking-tight">ExamDex</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <span onClick={() => navigate('/exams')} className="text-sm text-muted-foreground hover:text-foreground cursor-pointer transition-colors">Exams</span>
            <span onClick={() => navigate('/practice')} className="text-sm text-muted-foreground hover:text-foreground cursor-pointer transition-colors">Practice</span>
            <a href={DISCORD_INVITE_URL} target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground hover:text-foreground cursor-pointer transition-colors">Community</a>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={toggleDarkMode} className="p-2 rounded-full hover:bg-muted transition-colors">
              {isDark ? <Sun className="w-4 h-4 text-foreground" /> : <Moon className="w-4 h-4 text-foreground" />}
            </button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/auth?mode=login')} className="hidden sm:inline-flex">Log in</Button>
            <Button size="sm" onClick={() => navigate('/auth?mode=signup')}>Get started</Button>
          </div>
        </div>
      </nav>

      {/* ═══════════════════ HERO ═══════════════════ */}
      <section className="pt-32 pb-8 px-6 relative" ref={heroRef} onMouseMove={handleHeroMouse}>
        <div className="pointer-events-none absolute inset-0 transition-opacity duration-700" style={{ background: `radial-gradient(600px circle at ${mousePos.x * 100}% ${mousePos.y * 100}%, hsl(217 91% 60% / 0.06), transparent 60%)`, opacity: heroReady ? 1 : 0 }} />

        <div className="max-w-[1400px] mx-auto text-center relative">
          <h1 className={`text-[clamp(2.5rem,6vw,5.5rem)] font-bold text-foreground leading-[1.05] tracking-tight max-w-4xl mx-auto transition-all duration-700 ease-out ${heroReady ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            Your path to cracking{' '}
            <span className="bg-gradient-to-r from-primary via-blue-400 to-primary bg-[length:200%_auto] bg-clip-text text-transparent animate-[shimmer_3s_ease-in-out_infinite]">competitive exams</span>
          </h1>
          <p className={`mt-6 text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed transition-all duration-700 ease-out delay-150 ${heroReady ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            Practice with real exam questions, track your progress with detailed analytics, and challenge friends. Built for JEE, NEET & {examCount}+ exams.
          </p>
          <div className={`mt-10 flex flex-col sm:flex-row gap-4 justify-center transition-all duration-700 ease-out delay-300 ${heroReady ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <Button size="lg" onClick={() => navigate('/auth?mode=signup')} className="h-14 px-8 text-base rounded-full">
              Start practicing
              <ArrowRight className="ml-2" size={18} />
            </Button>
            <Button size="lg" variant="outline" className="h-14 px-8 text-base rounded-full" onClick={() => document.querySelector('[data-section="feat-practice"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}>
              See how it works
            </Button>
          </div>

          {/* Hero Image */}
          <div className={`mt-16 relative transition-all duration-1000 ease-out delay-500 ${heroReady ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
            <div className="relative rounded-2xl overflow-hidden border border-border/60 shadow-2xl">
              
              <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent opacity-60" />
            </div>
            <div className="absolute -inset-4 -z-10 rounded-3xl bg-primary/5 blur-3xl" />
          </div>
        </div>
      </section>

      {/* ═══════════════════ FEATURE SECTIONS ═══════════════════ */}
      {features.map((feat, idx) => {
        const reverse = idx % 2 === 1;
        return (
          <section
            key={feat.key}
            data-section={feat.key}
            ref={setSectionRef(feat.key)}
            className="py-20 md:py-32 px-6 border-t border-border/30"
          >
            <div className={`max-w-[1200px] mx-auto flex flex-col ${reverse ? 'md:flex-row-reverse' : 'md:flex-row'} items-center gap-12 md:gap-20`}>
              {/* Text side */}
              <div className={`flex-1 space-y-5 transition-all duration-700 ease-out ${isVisible(feat.key) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                <span className={`text-sm font-semibold uppercase tracking-wider ${feat.color}`}>{feat.label}</span>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground tracking-tight leading-tight">{feat.title}</h2>
                <p className="text-lg text-muted-foreground leading-relaxed max-w-lg">{feat.desc}</p>
                <div className="pt-2">
                  <Button variant="outline" className="rounded-full h-12 px-6 group" onClick={() => navigate(feat.href)}>
                    {feat.cta}
                    <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </div>

              {/* Mock side */}
              <div className={`flex-1 flex justify-center transition-all duration-700 ease-out delay-200 ${isVisible(feat.key) ? 'opacity-100 translate-x-0' : `opacity-0 ${reverse ? '-translate-x-10' : 'translate-x-10'}`}`}>
                <feat.Mock animate={isVisible(feat.key)} />
              </div>
            </div>
          </section>
        );
      })}

      

      {/* ═══════════════════ DISCORD ═══════════════════ */}
      <section className="py-16 px-6" data-section="discord" ref={setSectionRef('discord')}>
        <div className={`max-w-[1200px] mx-auto transition-all duration-700 ${isVisible('discord') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="rounded-3xl p-10 md:p-16 flex flex-col md:flex-row items-center gap-8" style={{ background: 'linear-gradient(135deg, #5865F2, #4752C4)' }}>
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-3xl md:text-4xl font-bold text-white">Join the community</h3>
              <p className="mt-3 text-white/70 text-lg max-w-xl">Share doubts, trade tips, find study partners, and stay motivated with thousands of students.</p>
            </div>
            <a
              href={DISCORD_INVITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white font-bold rounded-full hover:bg-white/90 transition-all text-lg"
              style={{ color: '#5865F2' }}
            >
              <MessageCircle size={20} />
              Join Discord
            </a>
          </div>
        </div>
      </section>

      {/* ═══════════════════ FOOTER ═══════════════════ */}
      <footer className="border-t border-border/50 py-16 px-6">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-12">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <img src="/logo192.png" alt="ExamDex" className="w-7 h-7 object-contain" />
                <span className="font-bold text-foreground">ExamDex</span>
              </div>
              <p className="text-sm text-muted-foreground">Exam prep for every Indian student.</p>
            </div>
            <div>
              <h4 className="font-semibold text-foreground text-sm mb-4">Product</h4>
              <ul className="space-y-2.5">
                {[['Practice Tests', '/practice'], ['Mistake Tracker', '/mistakes'], ['Analytics', '/analytics']].map(([label, path]) => (
                  <li key={label}><span onClick={() => navigate(path)} className="text-sm text-muted-foreground hover:text-foreground cursor-pointer transition-colors">{label}</span></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-foreground text-sm mb-4">Exams</h4>
              <ul className="space-y-2.5">
                {['JEE Main', 'NEET UG', 'GATE', 'CUET'].map(exam => (
                  <li key={exam}><span onClick={() => navigate('/exams')} className="text-sm text-muted-foreground hover:text-foreground cursor-pointer transition-colors">{exam}</span></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-foreground text-sm mb-4">Company</h4>
              <ul className="space-y-2.5">
                {[['Privacy', '/privacy'], ['Terms', '/terms'], ['Contact', '/contact']].map(([label, path]) => (
                  <li key={label}><span onClick={() => navigate(path)} className="text-sm text-muted-foreground hover:text-foreground cursor-pointer transition-colors">{label}</span></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-foreground text-sm mb-4">Community</h4>
              <ul className="space-y-2.5">
                <li><a href={DISCORD_INVITE_URL} target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Discord</a></li>
                <li><a href="mailto:support@examdex.in" className="text-sm text-muted-foreground hover:text-foreground transition-colors">support@examdex.in</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-border/50 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} ExamDex. All rights reserved.</p>
            <p className="text-xs text-muted-foreground flex items-center gap-1">Made with <Heart size={10} className="text-red-500 fill-red-500" /> in India</p>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes shimmer {
          0%, 100% { background-position: 0% center; }
          50% { background-position: 100% center; }
        }
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default Landing;
